import React, { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, Controller, useWatch } from "react-hook-form";
import { AnimatePresence, motion } from "framer-motion";
import { createHttpClient } from "../../api/client";
import { fetchMasterOptions } from "../../api/masterdata";
import { useMasterOptions } from "../../hooks/useMasterOptions";
import { useSessionContext } from "../../contexts/session-context";
import config from "../../config/env";
import { FileText } from "lucide-react";

import { ReactComponent as Right } from "../../assets/images/right.svg";
import { ReactComponent as Gallery } from "../../assets/images/Gallery.svg";
import { ReactComponent as SmartPhone } from "../../assets/images/Smartphone.svg";
import { ReactComponent as BankNote } from "../../assets/images/Banknote.svg";

import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import th from "date-fns/locale/th";

import {
  X,
  CheckCircle2,
  CalendarDays,
  ImagePlus,
  ChevronDown,
  Home,
  Copy,
} from "lucide-react";

registerLocale("th", th);

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function buildProductDisplayName(detail) {
  const productName = String(detail?.product_name || "").trim();
  const categoryName = String(detail?.model?.category?.name || detail?.category?.name || "").trim();
  const modelName = String(detail?.model?.name || "").trim();
  if (productName) return productName;
  if (categoryName && modelName) return `${categoryName} ${modelName}`;
  return categoryName || modelName || "สร้างออเดอร์";
}

function ensureSelectedOption(options, selectedValue, selectedLabel) {
  if (!selectedValue) return options;
  const exists = (options || []).some((o) => String(o.value) === String(selectedValue));
  if (exists) return options;
  return [{ value: String(selectedValue), label: String(selectedLabel || "-") }, ...(options || [])];
}

function formatThaiDateInput(raw) {
  const sanitized = String(raw || "").replace(/[^\d/]/g, "");
  if (!sanitized) return "";

  const slashCount = (sanitized.match(/\//g) || []).length;
  const parts = sanitized.split("/");
  const day = String(parts[0] || "").replace(/\D/g, "").slice(0, 2);
  const month = String(parts[1] || "").replace(/\D/g, "").slice(0, 2);
  const year = String(parts[2] || "").replace(/\D/g, "").slice(0, 4);

  let out = day;
  if (day.length < 2) return out;
  out += "/";
  if (month) out += month;
  if (month && (slashCount >= 2 || month.length >= 2 || year)) out += "/";
  if (year) out += year;
  return out;
}

function formatThaiDateBuddhist(d) {
  const dt = d instanceof Date ? d : toDateValue(d);
  if (!dt) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear() + 543;
  return `${dd}/${mm}/${yyyy}`;
}

function parseThaiDateString(v) {
  const m = String(v || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;

  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const buddhistYear = Number(m[3]);
  if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(buddhistYear)) return null;
  if (dd < 1 || dd > 31 || mm < 1 || mm > 12) return null;

  const yyyy = buddhistYear > 2400 ? buddhistYear - 543 : buddhistYear;
  const d = new Date(yyyy, mm - 1, dd);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return d;
}

function getThaiBuddhistInputValue(v) {
  if (v == null || v === "") return "";
  if (v instanceof Date) return formatThaiDateBuddhist(v);
  return String(v);
}

function toDateValue(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const thai = parseThaiDateString(v);
  if (thai) return thai;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateDisplay(v) {
  const d = toDateValue(v);
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function toNumber(v) {
  const raw = String(v ?? "").replace(/,/g, "").trim();
  const m = raw.match(/-?\d+(?:\.\d+)?/);
  const n = m ? Number(m[0]) : NaN;
  return Number.isFinite(n) ? n : 0;
}

const HARD_WARRANTY_OPTIONS = [
  { value: "ประกันศูนย์ 1 ปี", label: "ประกันศูนย์ 1 ปี" },
  { value: "ประกันศูนย์", label: "ประกันศูนย์" },
  { value: "ประกันหมด", label: "ประกันหมด" },
];


function normalizeWarrantyType(value) {
  const v = String(value || "").trim();
  if (!v) return "";
  if (v === "ประกันเหลือ" || v === "ประกันศูนย์") return "ประกันศูนย์";
  if (v === "ประกันหมด") return "ประกันหมด";
  if (v.includes("ประกันศูนย์")) return "ประกันศูนย์ 1 ปี";
  return "";
}

function normalizeUploadUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = String(config?.apiImg || "").replace(/\/+$/, "");
  if (!base) return url;
  if (url.startsWith("/")) return `${base}${url}`;
  return `${base}/${url}`;
}

function extractImageIdsFromApi(images) {
  if (!Array.isArray(images)) return [];
  return images
    .map((x) => {
      if (x == null) return null;
      if (typeof x === "number") return x;
      if (typeof x === "string") return x;
      if (typeof x === "object") {
        if (x.id != null) return x.id;
        if (x.data?.id != null) return x.data.id;
        if (x.attributes?.id != null) return x.attributes.id;
      }
      return null;
    })
    .filter((v) => v != null);
}

async function fetchUploadFileById(http, id) {
  if (id == null || id === "") return "";
  try {
    const res = await http.get(`/upload/files/${id}`);
    const file = res?.data;
    return normalizeUploadUrl(file?.url || file?.data?.url || "");
  } catch (e) {
    console.error(`load upload file error id=${id}:`, e);
    return "";
  }
}

function SectionCard({ icon: Icon, title, right, children }) {
  return (
    <div className="rounded-md border border-[#00000026] bg-white shadow-sm my-4">
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <div className="flex items-center gap-2">
          {Icon && (
            <span className="inline-flex items-center justify-center">
              <Icon size={18} className="text-slate-700" />
            </span>
          )}
          <div className="font-semibold text-slate-900">{title}</div>
        </div>
        {right}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}

const UnderlineDateInput = forwardRef(function UnderlineDateInput(
  { value, onClick, onChange, onBlur, placeholder, hasError, showTime, disabled },
  ref
) {
  const isEmpty = !value;

  return (
    <input
      type="text"
      ref={ref}
      disabled={disabled}
      onClick={onClick}
      onChange={onChange}
      onBlur={onBlur}
      value={value || ""}
      placeholder={placeholder || (showTime ? "เลือกวัน/เวลา" : "เลือกวันที่")}
      className={classNames(
        "mt-1 w-full bg-transparent text-sm outline-none",
        "border-b hover:border-[#047DED]",
        "min-h-[36px] text-left",
        hasError ? "border-red-500" : "border-[#00000026]",
        isEmpty ? "text-[#1D161680]" : "text-slate-900",
        disabled && "opacity-60 cursor-not-allowed"
      )}
    />
  );
});

function UnderlineDatePicker({
  label,
  name,
  control,
  errors,
  required = false,
  placeholder = "",
  showTimeSelect = false,
  timeIntervals = 5,
  dateFormat = "dd/MM/yyyy",
  disabled = false,
  thaiBuddhistInput = false,
}) {
  const hasError = !!errors?.[name];

  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
      <Controller
        control={control}
        name={name}
        rules={{ required: required ? "required" : false }}
        render={({ field }) => {
          if (thaiBuddhistInput) {
            return (
              <input
                type="text"
                disabled={disabled}
                value={getThaiBuddhistInputValue(field.value)}
                placeholder={placeholder}
                onChange={(e) => {
                  const next = formatThaiDateInput(e?.target?.value || "");
                  field.onChange(next);
                }}
                onBlur={field.onBlur}
                className={classNames(
                  "mt-1 w-full bg-transparent text-sm outline-none",
                  "border-b hover:border-[#047DED]",
                  hasError ? "border-red-500" : "border-[#00000026]",
                  disabled && "opacity-60 cursor-not-allowed"
                )}
              />
            );
          }

          return (
            <DatePicker
              selected={field.value ? toDateValue(field.value) : null}
              onChange={(d) => field.onChange(d)}
              onBlur={field.onBlur}
              disabled={disabled}
              placeholderText={placeholder}
              locale="th"
              showTimeSelect={showTimeSelect}
              timeIntervals={timeIntervals}
              dateFormat={showTimeSelect ? "dd/MM/yyyy HH:mm" : dateFormat}
              wrapperClassName="w-full"
              popperClassName="z-[9999]"
              customInput={
                <UnderlineDateInput
                  hasError={hasError}
                  placeholder={placeholder}
                  showTime={showTimeSelect}
                  disabled={disabled}
                />
              }
              popperPlacement="bottom-start"
            />
          );
        }}
      />
      {hasError && (
        <div className="mt-1 text-xs text-red-600">
          {errors?.[name]?.message || "required"}
        </div>
      )}
    </div>
  );
}

function UnderlineInput({
  label,
  name,
  register,
  required,
  errors,
  placeholder,
  type = "text",
  disabled = false,
  readOnly = false,
  inputMode,
  maxLength,
  onlyDigits = false,
  maxValue,
  forceUppercase = false,
  forceAlphanumeric = false,
  suffix,
  suffixInValue = false,
}) {
  const hasError = !!errors?.[name];
  const shouldInlineSuffix = Boolean(suffix && suffixInValue && onlyDigits);
  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
      <div className="relative mt-1">
        <input
          type={type}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          autoCapitalize={forceUppercase || forceAlphanumeric ? "characters" : "off"}
          inputMode={inputMode}
          placeholder={placeholder}
          onInput={(e) => {
            if (onlyDigits) {
              const raw = String(e.currentTarget.value || "").replace(/\D/g, "");
              if (!raw) {
                e.currentTarget.value = "";
                return;
              }
              const num = Number(raw);
              e.currentTarget.value =
                Number.isFinite(maxValue) && Number.isFinite(num)
                  ? String(Math.min(num, Number(maxValue)))
                  : raw;
            }

            if (forceUppercase || forceAlphanumeric) {
              e.currentTarget.value = String(e.currentTarget.value || "").toUpperCase();
            }

            if (forceAlphanumeric) {
              e.currentTarget.value = String(e.currentTarget.value || "").replace(/[^A-Z0-9]/g, "");
            }

            if (shouldInlineSuffix) {
              const digits = String(e.currentTarget.value || "").replace(/\D/g, "");
              e.currentTarget.value = digits ? `${digits}${suffix}` : "";
              if (e.currentTarget === document.activeElement) {
                const cursorPos = digits.length;
                e.currentTarget.setSelectionRange(cursorPos, cursorPos);
              }
            }
          }}
          onKeyDown={(e) => {
            if (!shouldInlineSuffix) return;
            if (e.key !== "Backspace" && e.key !== "Delete") return;

            const el = e.currentTarget;
            const digits = String(el.value || "").replace(/\D/g, "");
            if (!digits) {
              e.preventDefault();
              el.value = "";
              return;
            }

            const nextDigits = digits.slice(0, -1);
            e.preventDefault();
            el.value = nextDigits ? `${nextDigits}${suffix}` : "";
            if (el === document.activeElement) {
              const cursorPos = nextDigits.length;
              el.setSelectionRange(cursorPos, cursorPos);
            }
          }}
          className={classNames(
            "w-full bg-transparent text-sm outline-none placeholder:text-[#1D161680] hover:border-[#047DED]",
            "border-b",
            hasError ? "border-red-500" : "border-[#00000026]",
            (forceUppercase || forceAlphanumeric) && "uppercase",
            suffix && !shouldInlineSuffix && "pr-4",
            disabled && "bg-slate-100 cursor-not-allowed text-[#1D161680]"
          )}
          {...register(name, {
            required: required ? "required" : false,
            validate: (v) => {
              if (!Number.isFinite(maxValue)) return true;
              const digits = String(v ?? "").replace(/\D/g, "");
              if (!digits) return true;
              return Number(digits) <= Number(maxValue) || `ต้องไม่เกิน ${maxValue}`;
            },
            setValueAs: (v) => {
              if (onlyDigits) {
                const digits = String(v ?? "").replace(/\D/g, "");
                if (!digits) return "";
                const num = Number(digits);
                if (Number.isFinite(maxValue) && Number.isFinite(num)) {
                  return String(Math.min(num, Number(maxValue)));
                }
                return digits;
              }
              let next = String(v ?? "");
              if (forceUppercase || forceAlphanumeric) next = next.toUpperCase();
              if (forceAlphanumeric) next = next.replace(/[^A-Z0-9]/g, "");
              return next;
            },
          })}
        />
        {suffix && !shouldInlineSuffix && (
          <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-sm text-[#1D161680]">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function UnderlineTextarea({ label, name, register, required, errors, placeholder, rows = 3 }) {
  const hasError = !!errors?.[name];
  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
      <textarea
        rows={rows}
        placeholder={placeholder}
        className={classNames(
          "mt-1 w-full resize-none bg-transparent text-sm outline-none placeholder:text-[#1D161680]",
          "border-b",
          hasError ? "border-red-500" : "border-[#00000026]"
        )}
        {...register(name, { required: required ? "required" : false })}
      />
    </div>
  );
}

function UnderlineSelect({
  label,
  name,
  register,
  required,
  errors,
  placeholder = "เลือกข้อมูล",
  options = [],
  disabled = false,
  multiple = false,
}) {
  const hasError = !!errors?.[name];
  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
      <div className="relative mt-1">
        <select
          multiple={multiple}
          disabled={disabled}
          className={classNames(
            "w-full appearance-none bg-transparent text-sm outline-none border-b pr-10 text-black",
            disabled ? "opacity-60 cursor-not-allowed" : "text-[#1D1616B2]",
            hasError ? "border-black" : "border-[#00000026]"
          )}
          {...register(name, { required: required ? "required" : false })}
        >
          {!multiple && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value} className="text-black">
              {o.label}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2">
          <ChevronDown className="h-4 w-4 opacity-60" />
        </span>
      </div>
      {hasError && (
        <div className="mt-1 text-xs text-red-600">
          {errors?.[name]?.message || "required"}
        </div>
      )}
    </div>
  );
}

function RadioRow({ name, register, required, errors, options }) {
  const hasError = !!errors?.[name];
  return (
    <div className="py-3">
      <div className={classNames("mt-2 space-y-2 border-b pb-3", hasError ? "border-red-500" : "border-[#00000026]")}>
        {options.map((opt) => (
          <label key={opt.value} className="flex items-center gap-2 text-sm text-slate-800">
            <input
              type="radio"
              value={opt.value}
              className="h-4 w-4"
              {...register(name, { required: required ? "required" : false })}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function SuccessModal({ open, secondsLeft, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[100] bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
          >
            <div className="w-full max-w-sm rounded-md bg-white p-6 shadow-xl">
              <div className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 size={34} className="text-emerald-600" />
                </div>
                <div className="mt-3 text-lg font-extrabold text-slate-900">บันทึกข้อมูลสำเร็จ</div>
                <div className="mt-1 text-sm text-slate-500">
                  ระบบจะย้อนกลับใน <span className="font-bold text-slate-900">{secondsLeft}</span> วินาที
                </div>
                <button
                  onClick={onClose}
                  className="mt-5 w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:opacity-95 active:scale-[0.99]"
                >
                  ปิด
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function DescriptionTemplateModal({ open, text, onClose, onCopy }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[100] bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
          >
            <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between gap-3">
                <div className="text-2xl font-bold text-slate-900">เทมเพลตคำอธิบายสินค้า</div>
                <button
                  type="button"
                  onClick={onClose}
                  className="h-10 w-10 rounded-xl bg-white inline-flex items-center justify-center hover:bg-slate-50"
                >
                  <X size={22} />
                </button>
              </div>

              <div className="px-6 py-5">
                <textarea
                  value={text || ""}
                  readOnly
                  rows={16}
                  className="w-full rounded-xl border border-slate-300 bg-white p-4 text-lg leading-8 outline-none resize-none"
                />
              </div>

              <div className="px-6 pb-6">
                <button
                  type="button"
                  onClick={onCopy}
                  className="h-14 w-full rounded-2xl bg-[#111111] text-white text-lg font-bold hover:opacity-95 active:scale-[0.99] inline-flex items-center justify-center gap-3"
                >
                  <Copy size={22} />
                  คัดลอก
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function optionLabel(options, value) {
  return (options || []).find((x) => String(x?.value) === String(value))?.label || "";
}

function renderDescriptionTemplate(template, variables) {
  const tpl = String(template || "");
  if (!tpl) return "";
  return tpl.replace(/\{([^}]+)\}/g, (_, rawKey) => {
    const key = String(rawKey || "").trim();
    return String(variables?.[key] ?? "");
  });
}

function PricingQueueForm({ mode, id }) {
  const navigate = useNavigate();
  const location = useLocation();
  const http = useMemo(() => createHttpClient(), []);
  const isCreate = mode === "create";
  const returnToPath =
    location?.state?.fromPricingQueue && location?.state?.returnTo
      ? String(location.state.returnTo)
      : "/app/products/stock";

  const {
    productOptions,
    modelOptions,
    colorOptions,
    capacityOptions,
    loading: masterLoading,
    error: masterError,
  } = useMasterOptions();

  const [filteredModelOptions, setFilteredModelOptions] = useState([]);
  const [filteredColorOptions, setFilteredColorOptions] = useState([]);
  const [filteredCapacityOptions, setFilteredCapacityOptions] = useState([]);

  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const usageOptions = useMemo(
    () => [
      { value: "new", label: "มือหนึ่ง" },
      { value: "used", label: "มือสอง" },
    ],
    []
  );

  const productTypeOptions = useMemo(
    () => [
      { value: "iphone", label: "iPhone" },
      { value: "ipad", label: "iPad" },
      { value: "macbook", label: "MacBook" },
      { value: "gadget", label: "Gadget" },
      { value: "apple_watch", label: "Apple Watch" },
    ],
    []
  );

  const [qualityOptions, setQualityOptions] = useState([]);
  const [warrantyOptions, setWarrantyOptions] = useState(HARD_WARRANTY_OPTIONS);

  const [loadingEquipments, setLoadingEquipments] = useState(false);
  const [loadingQualities, setLoadingQualities] = useState(false);
  const isHydratingFromApiRef = useRef(!isCreate);
  const [session] = useSessionContext();
  const currentRoleName = String(
    session?.roleName || localStorage.getItem("auth_role_name") || ""
  )
    .trim()
    .toLowerCase();
  const isSuperAdmin = currentRoleName === "superadmin";
  const canEditSellPrice = isSuperAdmin;

  const [apiErr, setApiErr] = useState("");
  const [loadingDetail, setLoadingDetail] = useState(!isCreate);
  const [productDetail, setProductDetail] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateText, setTemplateText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const isSoldLocked =
    !isCreate && String(productDetail?.product_status || "").trim().toLowerCase() === "sold";

  const defaultValues = useMemo(
    () => ({
      received_at: null,
      product_name: "",
      product_type: "",
      model: "",
      color: "",
      capacity: "",
      equipments: "",
      usage: "",
      quality_master: "",
      battery_max: "",
      cycle_count: "",
      warranty: "",
      warranty_expire: null,
      imei: "",
      admin_desc: "",
      website_desc: "",
      facebook_ref: "",
      price_cost: "",
      price: "",
      buy_price_cash: "",
      buy_price_transfer: "",
      buy_price_others: "",
      is_onsale: false,
      onsale_discount: "",
      is_recommend: false,
      installment: "",
      note: "",
    }),
    []
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues, mode: "onSubmit" });

  useEffect(() => {
    if (!isCreate) return;
    const currentReceivedAt = toDateValue(getValues("received_at"));
    if (currentReceivedAt) return;
    setValue("received_at", new Date(), {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [getValues, isCreate, setValue]);

  const selectedCategoryId = useWatch({ control, name: "product_name" });
  const selectedModelId = useWatch({ control, name: "model" });
  const selectedColorId = useWatch({ control, name: "color" });
  const selectedCapacityId = useWatch({ control, name: "capacity" });
  const isOnsale = !!useWatch({ control, name: "is_onsale" });
  const allValues = useWatch({ control });

  const selectedWarranty = useWatch({ control, name: "warranty" });
  const isWarrantyRemaining = String(selectedWarranty || "").trim() === "ประกันศูนย์";

  useEffect(() => {
    if (isWarrantyRemaining) return;
    setValue("warranty_expire", null);
  }, [isWarrantyRemaining, setValue]);

  useEffect(() => {
    if (isOnsale) return;
    setValue("onsale_discount", "");
  }, [isOnsale, setValue]);

  useEffect(() => {
    if (canEditSellPrice) return;
    setValue("price", "");
  }, [canEditSellPrice, setValue]);

  useEffect(() => {
    let ignore = false;

    const fetchMasters = async () => {
      try {
        setLoadingEquipments(true);
        setLoadingQualities(true);

        const eqOptions = await fetchMasterOptions({
          path: "/equipments",
          field: "name",
        });
        const qOptions = await fetchMasterOptions({
          path: "/qualities?sort[0]=sequence:asc&sort[1]=id:asc&pagination[pageSize]=100",
          field: "name",
        });

        if (ignore) return;

        setEquipmentOptions(eqOptions);

        setWarrantyOptions(HARD_WARRANTY_OPTIONS);

        setQualityOptions(qOptions);
      } catch (err) {
        console.error(err);
      } finally {
        if (!ignore) {
          setLoadingEquipments(false);
          setLoadingQualities(false);
        }
      }
    };

    fetchMasters();
    return () => {
      ignore = true;
    };
  }, [http]);

  const prevSelectedCategoryIdRef = useRef(undefined);
  useEffect(() => {
    let ignore = false;

    const fetchModelsByCategory = async () => {
      const prevCategoryId = prevSelectedCategoryIdRef.current;
      prevSelectedCategoryIdRef.current = selectedCategoryId;

      if (!selectedCategoryId) {
        setFilteredModelOptions([]);
        setFilteredColorOptions([]);
        setFilteredCapacityOptions([]);
        if (!isHydratingFromApiRef.current) {
          setValue("model", "");
          setValue("color", "");
          setValue("capacity", "");
        }
        return;
      }

      const shouldResetChildren =
        !isHydratingFromApiRef.current &&
        prevCategoryId !== undefined &&
        String(prevCategoryId) !== String(selectedCategoryId);

      try {
        const res = await http.get("/models", {
          params: {
            filters: { category: { id: { $eq: selectedCategoryId } } },
            sort: ["sequence:asc", "id:asc"],
            pagination: { pageSize: 200 },
          },
        });

        if (ignore) return;

        setFilteredModelOptions(
          (res?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.name || "-",
          }))
        );
      } catch (e) {
        console.error("load models by category error:", e);
        if (!ignore) setFilteredModelOptions([]);
      }
    };

    fetchModelsByCategory();

    return () => {
      ignore = true;
    };
  }, [http, selectedCategoryId, setValue]);

  const prevSelectedModelIdRef = useRef(undefined);

  useEffect(() => {
    let ignore = false;

    const fetchColorsAndCapacitiesByModel = async () => {
      const prevModelId = prevSelectedModelIdRef.current;
      prevSelectedModelIdRef.current = selectedModelId;

      if (!selectedModelId) {
        setFilteredColorOptions([]);
        setFilteredCapacityOptions([]);
        if (!isHydratingFromApiRef.current) {
          setValue("color", "");
          setValue("capacity", "");
        }
        return;
      }

      const shouldResetChildren =
        !isHydratingFromApiRef.current &&
        prevModelId !== undefined &&
        String(prevModelId) !== String(selectedModelId);

      try {
        const [cRes, capRes] = await Promise.all([
          http.get("/colors", {
            params: {
              filters: { model: { id: { $eq: selectedModelId } } },
              sort: ["sequence:asc", "id:asc"],
              pagination: { pageSize: 200 },
            },
          }),
          http.get("/capacities", {
            params: {
              filters: { model: { id: { $eq: selectedModelId } } },
              sort: ["sequence:asc", "id:asc"],
              pagination: { pageSize: 200 },
            },
          }),
        ]);

        if (ignore) return;

        setFilteredColorOptions(
          (cRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.name || "-",
          }))
        );

        setFilteredCapacityOptions(
          (capRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.size || "-",
          }))
        );

      } catch (e) {
        console.error("load color/capacity by model error:", e);
        if (!ignore) {
          setFilteredColorOptions([]);
          setFilteredCapacityOptions([]);
        }
      }
    };

    fetchColorsAndCapacitiesByModel();

    return () => {
      ignore = true;
    };
  }, [http, selectedModelId, setValue]);

  const displayedModelOptions = useMemo(() => {
    const base = selectedCategoryId ? filteredModelOptions : modelOptions;
    return ensureSelectedOption(base, selectedModelId, productDetail?.model?.name);
  }, [filteredModelOptions, modelOptions, productDetail?.model?.name, selectedCategoryId, selectedModelId]);

  const displayedProductOptions = useMemo(() => {
    return ensureSelectedOption(
      productOptions,
      selectedCategoryId,
      productDetail?.model?.category?.name || productDetail?.product_name
    );
  }, [productDetail?.model?.category?.name, productDetail?.product_name, productOptions, selectedCategoryId]);

  const displayedColorOptions = useMemo(() => {
    const base = selectedModelId ? filteredColorOptions : colorOptions;
    return ensureSelectedOption(base, selectedColorId, productDetail?.color?.name);
  }, [colorOptions, filteredColorOptions, productDetail?.color?.name, selectedColorId, selectedModelId]);

  const displayedCapacityOptions = useMemo(() => {
    const base = selectedModelId ? filteredCapacityOptions : capacityOptions;
    return ensureSelectedOption(base, selectedCapacityId, productDetail?.capacity?.size);
  }, [capacityOptions, filteredCapacityOptions, productDetail?.capacity?.size, selectedCapacityId, selectedModelId]);

  const displayedWarrantyOptions = useMemo(() => {
    return warrantyOptions;
  }, [warrantyOptions]);

  const selectedEquipmentId = useWatch({ control, name: "equipments" });

  const displayedEquipmentOptions = useMemo(() => {
    return ensureSelectedOption(
      equipmentOptions,
      selectedEquipmentId,
      productDetail?.equipments?.[0]?.name
    );
  }, [equipmentOptions, productDetail?.equipments, selectedEquipmentId]);

  useEffect(() => {
    if (!selectedModelId) {
      setTemplateText("");
      return;
    }

    let ignore = false;
    (async () => {
      try {
        const res = await http.get(`/models/${selectedModelId}`);
        if (ignore) return;
        const model = res?.data?.data || null;
        setTemplateText(String(model?.template ?? ""));
      } catch (e) {
        console.error("load model template error:", e);
        if (!ignore) setTemplateText("");
      }
    })();

    return () => {
      ignore = true;
    };
  }, [http, selectedModelId]);

  const resolvedTemplate = useMemo(() => {
    const category = optionLabel(displayedProductOptions, allValues?.product_name);
    const model = optionLabel(displayedModelOptions, allValues?.model);
    const color = optionLabel(displayedColorOptions, allValues?.color);
    const storage = optionLabel(displayedCapacityOptions, allValues?.capacity);
    const quality = optionLabel(qualityOptions, allValues?.quality_master);
    const inventory = optionLabel(displayedEquipmentOptions, allValues?.equipments);
    const usageLabel = optionLabel(usageOptions, allValues?.usage);
    const is_used = usageLabel ? `สินค้ามือ${usageLabel === "มือหนึ่ง" ? "หนึ่ง" : "สอง"}` : "";
    const priceValue = toNumber(allValues?.price);
    const batteryHealth = String(allValues?.battery_max || "").trim();
    const cycleCount = String(allValues?.cycle_count || "").trim();
    const warranty = String(allValues?.warranty || "").trim();
    const warrantyExpire = toDateValue(allValues?.warranty_expire);
    const insurance_expired_date = warranty
      ? (warrantyExpire ? `${warranty} ${formatDateDisplay(warrantyExpire)}` : warranty)
      : "";

    const variables = {
      name: [category, model].filter(Boolean).join(" ").trim(),
      storage,
      color,
      price: priceValue ? priceValue.toLocaleString("th-TH") : "0",
      is_used,
      quality,
      insurance_expired_date,
      battery_health: batteryHealth ? `${batteryHealth}%` : "",
      charge_cycles: cycleCount ? `- ${cycleCount}รอบ` : "",
      inventory,
      admin_description: String(allValues?.admin_desc || ""),
    };

    return renderDescriptionTemplate(templateText, variables);
  }, [
    allValues,
    displayedProductOptions,
    displayedModelOptions,
    displayedColorOptions,
    displayedCapacityOptions,
    displayedEquipmentOptions,
    qualityOptions,
    usageOptions,
    templateText,
  ]);

  useEffect(() => {
    if (isCreate || !id) return;

    let ignore = false;

    const fetchDetail = async () => {
      try {
        setLoadingDetail(true);
        setApiErr("");

        const res = await http.get(`/products/${id}`);
        const item = res?.data?.data || null;

        if (ignore || !item) return;

        setProductDetail(item);
        isHydratingFromApiRef.current = true;

        reset({
          received_at: item.received_at ? new Date(item.received_at) : null,
          product_name: item.model?.category?.id ? String(item.model.category.id) : "",
          product_type: item.product_type || "",
          model: item.model?.id ? String(item.model.id) : "",
          color: item.color?.id ? String(item.color.id) : "",
          capacity: item.capacity?.id ? String(item.capacity.id) : "",
          equipments:
            Array.isArray(item.equipments) && item.equipments.length > 0 && item.equipments[0]?.id != null
              ? String(item.equipments[0].id)
              : "",
          usage: item.usage || "",
          quality_master: item.quality_master?.id ? String(item.quality_master.id) : "",
          battery_max:
            item.battery_health !== null && item.battery_health !== undefined
              ? String(item.battery_health)
              : "",
          cycle_count:
            item.cycle_count !== null && item.cycle_count !== undefined
              ? String(item.cycle_count)
              : "",
          warranty: normalizeWarrantyType(item.warranty_info),
          warranty_expire: item.warranty_date ? formatThaiDateBuddhist(item.warranty_date) : "",
          imei: String(item.imei || "").toUpperCase(),
          admin_desc: item.admin_description || "",
          website_desc: item.website_description || "",
          facebook_ref: item.facebook_ref || "",
          price_cost:
            item.buy_price !== null && item.buy_price !== undefined
              ? String(item.buy_price)
              : "",
          price:
            item.sell_price !== null && item.sell_price !== undefined
              ? String(item.sell_price)
              : "",
          buy_price_cash:
            item.buy_price_cash !== null && item.buy_price_cash !== undefined
              ? String(item.buy_price_cash)
              : "",
          buy_price_transfer:
            item.buy_price_transfer !== null && item.buy_price_transfer !== undefined
              ? String(item.buy_price_transfer)
              : "",
          buy_price_others:
            item.buy_price_others !== null && item.buy_price_others !== undefined
              ? String(item.buy_price_others)
              : "",
          is_onsale: !!item.is_onsale,
          onsale_discount:
            item.onsale_discount !== null && item.onsale_discount !== undefined
              ? String(item.onsale_discount)
              : "",
          is_recommend: !!item.is_recommend,
          installment: item.installment_plan || "",
          note: item.notes || "",
        });

        const ids = extractImageIdsFromApi(item.images);
        const objects = Array.isArray(item.images) ? item.images : [];
        const mapped = objects.length
          ? objects
            .map((x) => {
              if (!x) return null;
              const uploadedId = x.id ?? x?.data?.id ?? x?.attributes?.id;
              const sourceUrl = x.url ?? x?.attributes?.url ?? x?.data?.attributes?.url;
              const url = normalizeUploadUrl(sourceUrl);
              return {
                localId: `server_${uploadedId ?? Math.random()}`,
                file: null,
                url: url || "",
                uploadedId: uploadedId ?? null,
              };
            })
            .filter(Boolean)
          : (
            await Promise.all(
              ids.map(async (imgId) => ({
                localId: `server_${imgId}`,
                file: null,
                url: await fetchUploadFileById(http, imgId),
                uploadedId: imgId,
              }))
            )
          ).filter(Boolean);

        setPhotos(mapped.slice(0, MAX_PHOTOS));
        setTimeout(() => {
          isHydratingFromApiRef.current = false;
        }, 0);
      } catch (err) {
        console.error(err);
        setApiErr("โหลดข้อมูลสินค้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
        isHydratingFromApiRef.current = false;
      } finally {
        if (!ignore) setLoadingDetail(false);
      }
    };

    fetchDetail();
    return () => {
      ignore = true;
    };
  }, [http, id, isCreate, reset]);

  const [photos, setPhotos] = useState([]);
  const inputUploadRef = useRef(null);
  const inputCameraRef = useRef(null);
  const MAX_PHOTOS = 50;

  function addFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setPhotos((prev) => {
      const remain = Math.max(0, MAX_PHOTOS - prev.length);
      const picked = files.slice(0, remain).map((f) => ({
        localId: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        file: f,
        url: URL.createObjectURL(f),
        uploadedId: null,
      }));
      return [...prev, ...picked];
    });
  }

  function removePhoto(photoId) {
    setPhotos((prev) => {
      const found = prev.find((x) => x.localId === photoId);
      if (found?.url && found?.file) URL.revokeObjectURL(found.url);
      return prev.filter((x) => x.localId !== photoId);
    });
  }

  async function uploadSingleFile(file) {
    const formData = new FormData();
    formData.append("files", file);

    const res = await http.post("/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const arr = Array.isArray(res?.data) ? res.data : [];
    const first = arr[0];
    if (!first?.id) throw new Error("อัปโหลดรูปไม่สำเร็จ (no id)");
    return first.id;
  }

  async function ensureImagesIds(currentPhotos) {
    const ids = [];
    for (const p of currentPhotos) {
      if (p?.uploadedId != null) {
        ids.push(p.uploadedId);
        continue;
      }
      if (p?.file) {
        const newId = await uploadSingleFile(p.file);
        ids.push(newId);
      }
    }
    return ids;
  }

  const [saving, setSaving] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(3);

  const onSubmit = async (values) => {
    if (isSoldLocked) return;
    try {
      setSaving(true);
      setApiErr("");

      const imageIds = await ensureImagesIds(photos);
      const selectedCategory = productOptions.find(
        (x) => String(x.value) === String(values.product_name)
      );
      const selectedModel = displayedModelOptions.find(
        (x) => String(x.value) === String(values.model)
      );
      const categoryName = String(
        selectedCategory?.label ||
        optionLabel(displayedProductOptions, values.product_name) ||
        productDetail?.model?.category?.name ||
        ""
      ).trim();
      const modelName = String(
        selectedModel?.label ||
        optionLabel(displayedModelOptions, values.model) ||
        productDetail?.model?.name ||
        ""
      ).trim();
      const combinedProductName =
        [categoryName, modelName].filter(Boolean).join(" ").trim() ||
        String(productDetail?.product_name || "").trim();

      if (!combinedProductName) {
        throw new Error("กรุณาเลือกชื่อสินค้าและรุ่นสินค้าให้ครบก่อนบันทึก");
      }

      const buyPriceValue = toNumber(values.price_cost);
      const sellPriceValue = canEditSellPrice
        ? toNumber(values.price)
        : isCreate
          ? 0
          : toNumber(productDetail?.sell_price);
      const hasSellPrice = sellPriceValue > 0;
      const nextProductStatus = hasSellPrice ? "available" : "draft";
      const warrantyExpireDate = toDateValue(values.warranty_expire);
      const warrantyEndDate =
        String(values.warranty || "").trim() === "ประกันศูนย์" && warrantyExpireDate
          ? warrantyExpireDate.toISOString().slice(0, 10)
          : null;

      const payload = {
        data: {
          product_name: combinedProductName,
          product_type: String(values.product_type || ""),
          category: values.product_name ? Number(values.product_name) : null,
          model: values.model ? Number(values.model) : null,
          color: values.color ? Number(values.color) : null,
          capacity: values.capacity ? Number(values.capacity) : null,
          equipments: values.equipments ? [Number(values.equipments)].filter((x) => Number.isFinite(x)) : [],

          usage: String(values.usage || ""),
          quality_master: values.quality_master ? Number(values.quality_master) : null,
          battery_health: toNumber(values.battery_max),
          cycle_count: toNumber(values.cycle_count),

          warranty_info: String(values.warranty || ""),
          warranty_date: warrantyEndDate,
          warranty_end_date: warrantyEndDate,

          imei: String(values.imei || "").trim().toUpperCase(),

          admin_description: String(values.admin_desc || ""),
          website_description: String(values.website_desc || ""),
          facebook_ref: String(values.facebook_ref || ""),

          buy_price: buyPriceValue,
          buy_price_cash: toNumber(values.buy_price_cash),
          buy_price_transfer: toNumber(values.buy_price_transfer),
          buy_price_others: toNumber(values.buy_price_others),
          sell_price: sellPriceValue,
          is_onsale: !!values.is_onsale,
          onsale_discount: values.is_onsale ? toNumber(values.onsale_discount) : 0,
          is_recommend: !!values.is_recommend,
          installment_plan: String(values.installment || ""),
          notes: String(values.note || ""),

          received_at:
            toDateValue(values.received_at)?.toISOString() || new Date().toISOString(),

          product_status: nextProductStatus,

          images: imageIds,
        },
      };

      if (isCreate) {
        await http.post(`/products`, payload);
      } else {
        await http.put(`/products/${id}`, payload);
      }

      setOpenSuccess(true);
      setSecondsLeft(3);

      let s = 3;
      const t = setInterval(() => {
        s -= 1;
        setSecondsLeft(s);
        if (s <= 0) {
          clearInterval(t);
          setOpenSuccess(false);
          navigate(returnToPath);
        }
      }, 1000);
    } catch (err) {
      console.error(err);
      const backendMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message;
      setApiErr(backendMessage || "บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setSaving(false);
    }
  };

  const onInvalidSubmit = React.useCallback((formErrors) => {
    const firstName = Object.keys(formErrors || {})[0];
    if (!firstName) return;

    const el = document.querySelector(`[name="${firstName}"]`);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (typeof el.focus === "function") {
      el.focus({ preventScroll: true });
    }
  }, []);

  const closeSuccess = () => {
    setOpenSuccess(false);
    navigate(returnToPath);
  };

  const onDelete = async () => {
    if (isCreate || !id || deleting) return;
    const ok = window.confirm("ยืนยันการลบรายการนี้ใช่หรือไม่?");
    if (!ok) return;
    try {
      setDeleting(true);
      setApiErr("");
      await http.delete(`/products/${id}`);
      navigate(returnToPath);
    } catch (err) {
      console.error(err);
      setApiErr("ลบรายการไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setDeleting(false);
    }
  };

  const onCopyTemplate = async () => {
    const text = String(resolvedTemplate || "");
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("copy template error:", err);
    }
  };

  if (loadingDetail) {
    return (
      <div className="space-y-4 bg-[#F5F5F5] p-4 min-h-full">
        <div className="px-4 py-10 text-center text-slate-500">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  const displayProductName = buildProductDisplayName(productDetail);
  const pageTitle = isCreate ? "สร้างออเดอร์" : displayProductName;
  const breadcrumbTitle = isCreate ? "สร้างออเดอร์" : displayProductName;
  const reportPdfUrl = normalizeUploadUrl(productDetail?.report_pdf_path || "");

  return (
    <div className="space-y-4 bg-[#F5F5F5] p-4 min-h-full">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Home size={16} />
          <span className="hover:underline cursor-pointer" onClick={() => navigate(returnToPath)}>
            หน้าหลัก
          </span>
          <span className="text-slate-300">/</span>
          <span className="font-semibold text-[#047DED]">{breadcrumbTitle}</span>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-4xl font-extrabold text-slate-900">
              {isCreate && <Right className="text-4xl inline-block mr-2 text-[#1D1616]" />}
              {pageTitle}
            </div>

            <div className="mt-1 flex items-center gap-2 text-slate-500">
              {!isCreate && <CalendarDays size={16} />}
              <span className="text-sm">
                {isCreate
                  ? "สร้างออเดอร์สำหรับสินค้าหลัก"
                  : `สร้างออเดอร์เมื่อ ${formatDateDisplay(productDetail?.received_at) || "-"}`}
              </span>
            </div>
          </div>
          {isSoldLocked && reportPdfUrl ? (
            <a
              href={reportPdfUrl}
              download
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#047DED0D] px-4 py-2 text-sm font-semibold text-[#047DED] hover:text-[#FFF] hover:bg-[#047DED]"
            >
              <FileText size={16} />
              บิล
            </a>
          ) : null}
        </div>

        {masterError && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {masterError}
          </div>
        )}

        {apiErr && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {apiErr}
          </div>
        )}
      </div>

      <div className="rounded-md border border-slate-50 bg-white px-5 py-3 shadow-md">
        <div className="flex items-center justify-between gap-3">
          <div className="text-lg font-semibold text-slate-900">บันทึกรายการ</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(returnToPath)}
              className="h-10 rounded-md border border-[#00000026] bg-white px-4 text-sm font-semibold text-slate-700 hover:shadow-sm active:scale-[0.98]"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={handleSubmit(onSubmit, onInvalidSubmit)}
              disabled={saving || isSoldLocked}
              className={classNames(
                "h-10 rounded-md px-5 text-sm font-semibold text-white shadow-sm active:scale-[0.98] hover:bg-[#4C4444]",
                saving || isSoldLocked ? "bg-slate-400" : "bg-slate-900 hover:opacity-95"
              )}
            >
              {saving ? "กำลังบันทึก..." : "บันทึก"}
            </button>
          </div>
        </div>
      </div>

      {isSoldLocked && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          รายการนี้ขายออกแล้ว จึงดูข้อมูลได้อย่างเดียวและไม่สามารถบันทึกแก้ไขได้
        </div>
      )}

      <fieldset disabled={isSoldLocked} className="min-w-0">
        <SectionCard
          icon={Gallery}
          title="รูปภาพสินค้า"
          right={<div className="text-sm text-slate-500">{photos.length}/{MAX_PHOTOS}</div>}
        >
          <div className="min-w-0">
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9">
              {photos.map((p, idx) => (
                <div key={p.localId} className="group relative">
                  <div className="w-full aspect-square overflow-hidden rounded-md border border-[#00000026] bg-slate-50">
                    <img src={p.url} alt="photo" className="h-full w-full object-cover" />
                  </div>

                  {idx === 0 && (
                    <div className="absolute left-1 top-1 rounded-lg bg-[#047DED] px-2 py-0.5 text-[11px] font-bold text-white">
                      ภาพปก
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => removePhoto(p.localId)}
                    className="absolute right-1 top-1 hidden h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white group-hover:inline-flex"
                    title="ลบ"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={() => inputUploadRef.current?.click()}
                disabled={photos.length >= MAX_PHOTOS}
                className={classNames(
                  "w-full aspect-square rounded-md bg-blue-50 flex flex-col items-center justify-center gap-1 text-[#047DED]",
                  "hover:shadow-sm active:scale-[0.98] transition hover:border-2 hover:border-[#047DED4D]",
                  photos.length >= MAX_PHOTOS && "opacity-50 cursor-not-allowed"
                )}
              >
                <ImagePlus size={22} className="text-[#047DED]" />
                <span className="text-[11px] font-semibold text-[#047DED]">เพิ่มรูปภาพ</span>
              </button>
            </div>

            <input
              ref={inputUploadRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            <input
              ref={inputCameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
          </div>
        </SectionCard>

        <SectionCard icon={SmartPhone} title="รายละเอียดสินค้า">
          <div className="grid grid-cols-1">
            <UnderlineDatePicker
              label="สินค้าเข้าเมื่อวันที่"
              name="received_at"
              control={control}
              errors={errors}
              placeholder="เลือกวันที่"
              showTimeSelect
            />

            <UnderlineSelect
              label="ชื่อสินค้า"
              name="product_name"
              register={register}
              required
              errors={errors}
              options={displayedProductOptions}
              disabled={masterLoading}
              placeholder={masterLoading ? "กำลังโหลด..." : "เลือกข้อมูล"}
            />

            <UnderlineSelect
              label="ประเภทสินค้า (เมนูหน้าบ้าน)"
              name="product_type"
              register={register}
              required
              errors={errors}
              options={productTypeOptions}
              placeholder="เลือกประเภทสินค้า"
            />

            <UnderlineSelect
              label="Model"
              name="model"
              register={register}
              required
              errors={errors}
              options={displayedModelOptions}
              disabled={masterLoading}
              placeholder={masterLoading ? "กำลังโหลด..." : "เลือกข้อมูล"}
            />

            <UnderlineSelect
              label="สี"
              name="color"
              register={register}
              required
              errors={errors}
              options={displayedColorOptions}
              disabled={masterLoading || !selectedModelId}
              placeholder={masterLoading ? "กำลังโหลด..." : "เลือกข้อมูล"}
            />

            <UnderlineSelect
              label="ความจุ"
              name="capacity"
              register={register}
              required
              errors={errors}
              options={displayedCapacityOptions}
              disabled={masterLoading || !selectedModelId}
              placeholder={masterLoading ? "กำลังโหลด..." : "เลือกข้อมูล"}
            />

            <UnderlineSelect
              label="อุปกรณ์"
              name="equipments"
              register={register}
              required
              errors={errors}
              options={displayedEquipmentOptions}
              disabled={loadingEquipments}
              placeholder={loadingEquipments ? "กำลังโหลด..." : "เลือกข้อมูล"}
            />

            <UnderlineSelect
              label="ผ่านการใช้งาน"
              name="usage"
              register={register}
              required
              errors={errors}
              options={usageOptions}
              placeholder="เลือกข้อมูล"
            />

            <UnderlineSelect
              label="คุณภาพสินค้า"
              name="quality_master"
              register={register}
              required
              errors={errors}
              options={qualityOptions}
              disabled={loadingQualities}
              placeholder={loadingQualities ? "กำลังโหลด..." : "เลือกข้อมูล"}
            />

            <UnderlineInput
              label="ความจุแบตเตอรี่สูงสุด"
              name="battery_max"
              register={register}
              required
              errors={errors}
              placeholder="กรอกข้อมูล"
              inputMode="numeric"
              onlyDigits
              maxValue={100}
              suffix=" %"
              suffixInValue
            />

            <UnderlineInput
              label="รอบชาร์จ"
              name="cycle_count"
              register={register}
              errors={errors}
              placeholder="กรอกข้อมูล"
              inputMode="numeric"
              onlyDigits
            />

            <UnderlineSelect
              label="ประกัน"
              name="warranty"
              register={register}
              errors={errors}
              options={displayedWarrantyOptions}
              placeholder="เลือกข้อมูล"
            />

            {isWarrantyRemaining && (
              <UnderlineDatePicker
                label="วันหมดอายุประกัน"
                name="warranty_expire"
                control={control}
                errors={errors}
                placeholder="วัน/เดือน/ปีพ.ศ."
                thaiBuddhistInput
              />
            )}

            <UnderlineInput
              label="IMEI"
              name="imei"
              register={register}
              errors={errors}
              placeholder="กรอกข้อมูล"
              maxLength={15}
              forceUppercase
              forceAlphanumeric
            />

            <UnderlineTextarea
              label="คำอธิบาย: โดยผู้ดูแล"
              name="admin_desc"
              register={register}
              errors={errors}
              rows={5}
              placeholder="พิมพ์ได้หลายบรรทัด..."
            />

            <UnderlineTextarea
              label="รายละเอียดสินค้าสำหรับเว็บไซต์"
              name="website_desc"
              register={register}
              errors={errors}
              rows={4}
              placeholder="พิมพ์ได้หลายบรรทัด..."
            />

            <UnderlineInput
              label="Facebook Reference"
              name="facebook_ref"
              register={register}
              errors={errors}
              placeholder="กรอกข้อมูล"
            />
          </div>
        </SectionCard>

        <SectionCard icon={BankNote} title="ราคาสินค้า">
          <div className="grid grid-cols-1">
            <UnderlineInput
              label="ราคาทุน"
              name="price_cost"
              register={register}
              // required
              errors={errors}
              inputMode="numeric"
              onlyDigits
              placeholder="กรอกข้อมูล"
            // disabled={!isCreate}รายละเอียดสินค้ารายละเอียดสินค้า
            />
            {canEditSellPrice && (
              <UnderlineInput
                label="ราคาขาย"
                name="price"
                register={register}
                // required
                errors={errors}
                inputMode="numeric"
                onlyDigits
                placeholder="กรอกข้อมูล"
              />
            )}

            <UnderlineInput
              label="เงินสด"
              name="buy_price_cash"
              register={register}
              errors={errors}
              inputMode="numeric"
              onlyDigits
              placeholder="กรอกข้อมูล"
            />

            <UnderlineInput
              label="เงินโอน"
              name="buy_price_transfer"
              register={register}
              errors={errors}
              inputMode="numeric"
              onlyDigits
              placeholder="กรอกข้อมูล"
            />

            <UnderlineInput
              label="อื่นๆ"
              name="buy_price_others"
              type="text"
              register={register}
              errors={errors}
              placeholder="กรอกข้อมูล"
            />

            <UnderlineInput
              label="เรทผ่อน"
              name="installment"
              register={register}
              placeholder="กรอกรายละเอียดเรทผ่อน"
              errors={errors}
            />

            <div className="py-3">
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <input type="checkbox" className="h-4 w-4" {...register("is_onsale")} />
                <span className="font-semibold">สินค้านี้กำลังลดราคา (ราคาลดเหลือ)</span>
              </label>
            </div>

            {isOnsale && (
              <UnderlineInput
                label="ราคาที่ลดเหลือ"
                name="onsale_discount"
                register={register}
                required
                errors={errors}
                placeholder="0.00"
                type="number"
              />
            )}

            <div className="py-3">
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <input type="checkbox" className="h-4 w-4" {...register("is_recommend")} />
                <span className="font-semibold">สินค้านี้เป็นสินค้าแนะนำ</span>
              </label>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="เพิ่มเติม">
          <UnderlineTextarea
            label="หมายเหตุ"
            name="note"
            register={register}
            errors={errors}
            rows={4}
          />

          <div className="space-y-3 pt-1 w-full text-center justify-center items-center">
            <button
              type="button"
              onClick={() => setTemplateOpen(true)}
              className="w-full rounded-md bg-[#F3F4F6] px-4 py-6 text-left text-sm font-semibold text-[#047DED] hover:opacity-90 underline"
            >
              เทมเพลตคำอธิบายสินค้า
            </button>
            {!isCreate && (
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="mx-auto self-center px-4 py-3 text-center text-sm font-semibold text-rose-600 hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
              >
                {/* <Trash2 size={16} /> */}
                {deleting ? "กำลังลบรายการ..." : "ลบรายการ"}
              </button>
            )}
          </div>

        </SectionCard>

        <DescriptionTemplateModal
          open={templateOpen}
          text={resolvedTemplate}
          onClose={() => setTemplateOpen(false)}
          onCopy={onCopyTemplate}
        />
      </fieldset>
      <SuccessModal open={openSuccess} secondsLeft={secondsLeft} onClose={closeSuccess} />
    </div>
  );
}

export default function PricingQueueDetail() {
  const params = useParams();
  const location = useLocation();
  const { id } = params || {};

  const pathname = location.pathname || "";
  const idLower = String(id || "").toLowerCase();

  const isCreate =
    !id ||
    idLower === "create" ||
    pathname.includes("/pricing-queue/create");

  return isCreate
    ? <PricingQueueForm mode="create" />
    : <PricingQueueForm mode="detail" id={id} />;
}
